/**
 * Auto Floor Tool
 *
 * Automatically detects floors in the building using ray-casting and RANSAC.
 * Now includes a selection stage where user selects an area in top-down 2D view
 * before floor detection runs on the selected region.
 * Caches results in the point cloud geometry for reuse.
 */

import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { FloorToolBase } from './FloorToolBase';
import { DEFAULT_SLAB_THICKNESS_M } from '@pointorama/potree-shared';
import { DetectedFloorPlane, detectStoreys, visualizeDetectedLevels, buildFloorDetectionFixture, serializeFixtureLevels, fixtureLevelsEqual, FloorDetectionFixture, FLOOR_DETECTION_TUNING } from '../../../utils/floorDetection';
import { collectNodesInBox } from '../../../utils/floorDetection/columnHistogram';
import { lastFramePendingNodes, setLodFocusBox } from '../../../Potree_update_visibility';
import * as potreeState from '../../../potreeState';
import { FullScreenLoadingWidget } from '../../../utils/FullScreenLoadingWidget';
import { FloorVisualizationWidget } from '../../../utils/DebugWidgets';
import { createMultiViewConfig, ViewModes } from '../../helpers';
import { ClipTask } from '../../../defines';
import { CameraAlignmentTool } from '../../../utils/CameraAlignmentTool';
import { saveFloorDetectionArea } from '@pointorama/potree-shared';
import { BoxVolume } from '../../../utils/Volume';
import * as THREE from 'three';

/** Stage of the auto floor tool */
type AutoFloorStage = 'selecting' | 'detecting' | 'floor_selection' | 'all_added';

/** Point budget while the selection loads at full density (floor of it — a higher user budget wins). */
const FOCUS_POINT_BUDGET = 9_000_000;
/**
 * The focused load is DONE when the LOD traversal reports no pending nodes
 * (lastFramePendingNodes) and no loads in flight, held for this many
 * consecutive frames. The short hold only bridges hierarchy-metadata fetches
 * (untracked network round-trips that briefly look like "nothing to do").
 */
const FOCUS_LOAD_SETTLED_FRAMES = 30;
/**
 * Failsafe: nothing pending but the in-flight counter is stuck at the same
 * non-zero value for this long — a load whose decrement was lost must not
 * stall detection forever.
 */
const FOCUS_LOAD_STUCK_MS = 3_000;
/** Hard cap on the focused-load wait; detection then runs on whatever loaded. */
const FOCUS_LOAD_TIMEOUT_MS = 45_000;
/**
 * One continuous loading bar: the focused load fills the first half, the
 * detection itself the second — a bar that resets between phases reads as
 * "starting over" to the user.
 */
const LOAD_PHASE_PROGRESS_PERCENT = 50;

export class AutoFloorTool extends FloorToolBase {
  readonly name = 'auto_floor';

  private loadingWidget: FullScreenLoadingWidget;
  private selectedFloorClipBoxes: BoxVolume[] = []; // SHOW_OUTSIDE boxes hiding confirmed floors
  private floorDebugWidget: FloorVisualizationWidget | null = null;
  private isDetecting = false;
  private shouldCancelDetection = false;
  /**
   * Regression fixture of the last finished detection run, snapshotted the
   * moment detection completes — the preview edit rig mutates the level
   * planes in place afterwards, and user corrections must not overwrite the
   * fixture's recorded raw pipeline output.
   */
  private lastDetectionFixture: FloorDetectionFixture | null = null;

  // Focused-load state: while detection prepares/runs, a SHOW_INSIDE clip box
  // over the selection concentrates the whole (raised) point budget there —
  // the LOD pass culls nodes outside SHOW_INSIDE boxes, so the rest of the
  // scene is temporarily thrown out and the selection loads at max density.
  private focusClipBox: BoxVolume | null = null;
  private savedPointBudget: number | null = null;
  private savedDynamicBudget = false;
  /** Bumped per detection run (and on disable) so a superseded async load loop never touches the new run's state. */
  private detectionSession = 0;

  /** Clip everything but the selection and pour the budget into it. */
  private focusOnSelection(boundingBox: THREE.Box3): void {
    const corners =
      this.selectedAreaCorners && this.selectedAreaCorners.length >= 4
        ? this.selectedAreaCorners.map((c) => new THREE.Vector2(c.x, c.y))
        : [new THREE.Vector2(boundingBox.min.x, boundingBox.min.y), new THREE.Vector2(boundingBox.max.x, boundingBox.min.y), new THREE.Vector2(boundingBox.max.x, boundingBox.max.y), new THREE.Vector2(boundingBox.min.x, boundingBox.max.y)];
    const [c0, c1, , c3] = corners;
    const edgeX = new THREE.Vector2().subVectors(c1, c0);
    const edgeY = new THREE.Vector2().subVectors(c3, c0);
    const center = new THREE.Vector2().addVectors(c0, corners[2]).multiplyScalar(0.5);

    const focusBox = new BoxVolume({
      position: new THREE.Vector3(center.x, center.y, (boundingBox.min.z + boundingBox.max.z) / 2),
      scale: new THREE.Vector3(edgeX.length(), edgeY.length(), boundingBox.max.z - boundingBox.min.z),
      rotation: new THREE.Vector3(0, 0, Math.atan2(edgeX.y, edgeX.x)),
      clip: true,
    });
    focusBox.assignedClipTask = ClipTask.SHOW_INSIDE;
    focusBox.actualClipTask = ClipTask.SHOW_INSIDE;
    focusBox.visible = false; // clip only — no box visuals
    focusBox.updateMatrixWorld(true);
    this.viewer.scene.addVolume(focusBox);
    this.focusClipBox = focusBox;

    this.savedPointBudget = this.viewer.getPointBudget();
    this.savedDynamicBudget = this.viewer.isDynamicPointBudgetEnabled();
    // The dynamic budget would shrink the boost again on the first slow frame.
    if (this.savedDynamicBudget) this.viewer.disableDynamicPointBudget();
    this.viewer.setPointBudget(Math.max(FOCUS_POINT_BUDGET, this.savedPointBudget));

    // Camera-independent refinement: without this, nodes outside the camera
    // frustum never load and far-away nodes never pass the screen-size gate,
    // so parts of the selection would stay coarse or empty.
    setLodFocusBox(boundingBox);
  }

  /** Undo focusOnSelection (idempotent — also called from onDisable). */
  private unfocusSelection(): void {
    setLodFocusBox(null);
    if (this.focusClipBox) {
      this.viewer.scene.removeVolume(this.focusClipBox);
      this.focusClipBox = null;
    }
    if (this.savedPointBudget !== null) {
      this.viewer.setPointBudget(this.savedPointBudget);
      this.savedPointBudget = null;
    }
    if (this.savedDynamicBudget) {
      this.viewer.enableDynamicPointBudget();
      this.savedDynamicBudget = false;
    }
  }

  /**
   * Wait until the LOD traversal reports the focused selection fully loaded:
   * zero pending nodes (unloaded or awaiting GPU upload) and zero loads in
   * flight. This is authoritative — an area holding fewer points than the
   * budget settles the moment its deepest nodes are in, instead of waiting
   * out a stability heuristic. Feeds the loading bar; returns false when
   * cancelled; timeout/stuck-loader failsafes proceed with what loaded.
   */
  private async waitForFocusedLoad(boundingBox: THREE.Box3, session: number): Promise<boolean> {
    const startedAt = performance.now();
    let settledFrames = 0;
    let stuckLoadingCount = -1;
    let stuckSince = 0;

    while (performance.now() - startedAt < FOCUS_LOAD_TIMEOUT_MS) {
      await new Promise(requestAnimationFrame);
      if (session !== this.detectionSession || this.shouldCancelDetection || !this.enabled) return false;

      const pending = lastFramePendingNodes;
      const loading = potreeState.numNodesLoading;

      // The final density is unknown up front (the area may hold fewer
      // points than the budget), so the bar approaches but never claims the
      // whole load share from the count alone — settling completes it.
      const nodes = collectNodesInBox(this.viewer.scene.pointclouds, boundingBox);
      let points = 0;
      for (const node of nodes) points += node.count;
      this.loadingWidget.updateProgress(Math.min(LOAD_PHASE_PROGRESS_PERCENT - 1, (LOAD_PHASE_PROGRESS_PERCENT * points) / this.viewer.getPointBudget()));

      if (pending === 0 && loading === 0) {
        settledFrames++;
        if (settledFrames >= FOCUS_LOAD_SETTLED_FRAMES) return true;
      } else {
        settledFrames = 0;
      }

      // Stuck-loader failsafe (see FOCUS_LOAD_STUCK_MS).
      if (pending === 0 && loading > 0 && loading === stuckLoadingCount) {
        if (performance.now() - stuckSince > FOCUS_LOAD_STUCK_MS) return true;
      } else {
        stuckLoadingCount = pending === 0 ? loading : -1;
        stuckSince = performance.now();
      }
    }
    return true;
  }

  /**
   * The fixture to export: the raw-output snapshot, plus the CURRENT state
   * of the proposals as `correctedLevels` when the user has edited them —
   * corrected heights are the ground truth the pipeline should eventually
   * reach, and the replay spec tracks them as a known-unreached target.
   */
  private buildExportFixture(): FloorDetectionFixture | null {
    const snapshot = this.lastDetectionFixture;
    if (!snapshot) return null;
    const current = serializeFixtureLevels(this.availableFloors);
    // availableFloors drops ceiling-less levels — compare like with like.
    const baseline = snapshot.levels.filter((level) => level.ceilingZ !== null);
    if (fixtureLevelsEqual(current, baseline)) return snapshot;
    return { ...snapshot, correctedLevels: current };
  }

  // Selection stage state
  private stage: AutoFloorStage = 'selecting';
  private selectionBox: HTMLDivElement | null = null;
  private areaLabel: HTMLDivElement | null = null;
  private isSelectingArea = false;
  private selectionStartScreen: THREE.Vector2 | null = null;
  private selectionStartWorld: THREE.Vector3 | null = null;
  private selectionCurrentScreen: THREE.Vector2 | null = null;
  private selectedBoundingBox: THREE.Box3 | null = null;
  private selectedAreaCorners: THREE.Vector3[] | null = null;

  // Camera alignment for screen rotation (click-click to rotate, drag to select)
  private cameraAlignmentTool: CameraAlignmentTool;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.loadingWidget = new FullScreenLoadingWidget(true);
    this.cameraAlignmentTool = new CameraAlignmentTool(viewer);
    this.createSelectionUI();
  }

  /**
   * Create the selection box UI elements
   */
  private createSelectionUI(): void {
    // Selection box element
    this.selectionBox = document.createElement('div');
    this.selectionBox.style.position = 'absolute';
    this.selectionBox.style.border = '2px dashed #59CD90';
    this.selectionBox.style.background = 'rgba(89, 205, 144, 0.1)';
    this.selectionBox.style.display = 'none';
    this.selectionBox.style.pointerEvents = 'none';
    this.selectionBox.style.zIndex = '1000';

    // Area label
    this.areaLabel = document.createElement('div');
    this.areaLabel.style.position = 'absolute';
    this.areaLabel.style.background = 'rgba(0, 0, 0, 0.8)';
    this.areaLabel.style.color = 'white';
    this.areaLabel.style.padding = '8px 12px';
    this.areaLabel.style.borderRadius = '4px';
    this.areaLabel.style.fontFamily = 'monospace';
    this.areaLabel.style.fontSize = '12px';
    this.areaLabel.style.pointerEvents = 'none';
    this.areaLabel.style.display = 'none';
    this.areaLabel.style.zIndex = '1001';
    this.areaLabel.style.textAlign = 'center';
  }

  /**
   * Convert screen coordinates to world coordinates on the XY plane
   */
  private screenToWorldXY(screenPos: THREE.Vector2): THREE.Vector3 | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    const ndcX = (screenPos.x / rect.width) * 2 - 1;
    const ndcY = -(screenPos.y / rect.height) * 2 + 1;

    const camera = this.viewer.scene.getActiveCamera();
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

    // Intersect with XY plane at z=0 (or average z of point cloud)
    const boundingBox = this.viewer.getBoundingBox(this.viewer.scene.pointclouds);
    const avgZ = (boundingBox.min.z + boundingBox.max.z) / 2;
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -avgZ);
    const target = new THREE.Vector3();

    if (raycaster.ray.intersectPlane(plane, target)) {
      return target;
    }
    return null;
  }

  /**
   * Calculate the area of the selection box in square meters
   */
  private calculateSelectionAreaM2(): number {
    if (!this.selectionStartWorld || !this.selectionCurrentScreen) return 0;

    const currentWorld = this.screenToWorldXY(this.selectionCurrentScreen);
    if (!currentWorld) return 0;

    const width = Math.abs(currentWorld.x - this.selectionStartWorld.x);
    const height = Math.abs(currentWorld.y - this.selectionStartWorld.y);
    return width * height;
  }

  protected onEnable(): void {
    super.onEnable();

    // Start in selection stage
    this.stage = 'selecting';
    this.selectedBoundingBox = null;
    this.dispatchStateChanged({ phase: this.stage });

    // Reset detection state
    this.isDetecting = false;
    this.shouldCancelDetection = false;

    // Clear stale results from previous runs
    this.detectedFloors = [];
    this.availableFloors = [];
    this.addedFloors = [];

    // Attach selection UI to render area
    if (this.selectionBox && !this.selectionBox.parentElement) {
      this.viewer.renderArea.appendChild(this.selectionBox);
    }
    if (this.areaLabel && !this.areaLabel.parentElement) {
      this.viewer.renderArea.appendChild(this.areaLabel);
    }

    // Set cursor to crosshair for selection
    this.viewer.renderArea.style.cursor = 'crosshair';

    // Add floor visualization debug widget
    if (this.viewer.debugOverlay && !this.floorDebugWidget) {
      this.floorDebugWidget = new FloorVisualizationWidget(this.viewer, () => this.buildExportFixture());
      this.viewer.debugOverlay.addWidget(this.floorDebugWidget);
    }
  }

  protected onDisable(): void {
    super.onDisable();

    // Cancel any ongoing detection
    if (this.isDetecting) {
      this.shouldCancelDetection = true;
      this.isDetecting = false;
    }
    this.loadingWidget.hide();

    // Cancel any ongoing camera alignment
    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.cancel();
    }

    // Clean up selection UI
    this.hideSelectionUI();
    if (this.selectionBox?.parentElement) {
      this.selectionBox.parentElement.removeChild(this.selectionBox);
    }
    if (this.areaLabel?.parentElement) {
      this.areaLabel.parentElement.removeChild(this.areaLabel);
    }

    // Reset selection state
    this.isSelectingArea = false;
    this.selectionStartScreen = null;
    this.selectionStartWorld = null;
    this.selectionCurrentScreen = null;
    this.selectedBoundingBox = null;
    this.selectedAreaCorners = null;
    this.stage = 'selecting';

    // Reset cursor
    this.viewer.renderArea.style.cursor = 'default';

    // Remove floor visualization debug widget; drop the kept run with it
    // (the histogram grid holds a few MB of bins).
    if (this.viewer.debugOverlay && this.floorDebugWidget) {
      this.viewer.debugOverlay.removeWidget(this.floorDebugWidget.name);
      this.floorDebugWidget = null;
    }
    this.lastDetectionFixture = null;

    // Safety: release the focused-load clip/budget if the tool is disabled
    // mid-load, and invalidate any in-flight load loop (it also bails on its
    // own, but only on its next frame).
    this.detectionSession++;
    this.unfocusSelection();
  }

  /**
   * Hide selection UI elements
   */
  private hideSelectionUI(): void {
    if (this.selectionBox) {
      this.selectionBox.style.display = 'none';
    }
    if (this.areaLabel) {
      this.areaLabel.style.display = 'none';
    }
  }

  /**
   * Update the selection box visual based on current drag positions
   */
  private updateSelectionBoxVisual(): void {
    if (!this.selectionBox || !this.selectionStartScreen || !this.selectionCurrentScreen) return;

    const startX = this.selectionStartScreen.x;
    const startY = this.selectionStartScreen.y;
    const currentX = this.selectionCurrentScreen.x;
    const currentY = this.selectionCurrentScreen.y;

    const left = Math.min(startX, currentX);
    const top = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    this.selectionBox.style.left = `${left}px`;
    this.selectionBox.style.top = `${top}px`;
    this.selectionBox.style.width = `${width}px`;
    this.selectionBox.style.height = `${height}px`;
    this.selectionBox.style.display = 'block';

    // Update area label
    if (this.areaLabel) {
      const areaM2 = this.calculateSelectionAreaM2();
      this.areaLabel.textContent = `${areaM2.toFixed(1)} m²\nSelect area for floor detection`;
      this.areaLabel.style.left = `${left + width / 2}px`;
      this.areaLabel.style.top = `${top + height / 2}px`;
      this.areaLabel.style.transform = 'translate(-50%, -50%)';
      this.areaLabel.style.display = 'block';
      this.areaLabel.style.whiteSpace = 'pre-line';
    }
  }

  /**
   * Handle mouse down for selection stage
   */
  public onMouseDown(event: MouseEvent): void {
    // Floor-selection stage: the preview edit rig needs the press (handles)
    if (this.stage === 'floor_selection') {
      super.onMouseDown(event);
      return;
    }
    if (this.stage !== 'selecting') return;
    if (event.button !== 0) return; // Only left click

    // Don't start area selection while camera alignment is active
    if (this.cameraAlignmentTool.isAlignmentActive()) return;

    const rect = this.viewer.renderArea.getBoundingClientRect();
    const screenPos = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    const worldPos = this.screenToWorldXY(screenPos);

    if (worldPos) {
      this.isSelectingArea = true;
      this.selectionStartScreen = screenPos;
      this.selectionStartWorld = worldPos;
      this.selectionCurrentScreen = screenPos.clone();
    }
  }

  /**
   * Handle mouse move for selection stage
   */
  public onMouseMove(event: MouseEvent): void {
    if (this.stage === 'selecting') {
      // Update camera alignment preview if active
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        const rect = this.viewer.renderArea.getBoundingClientRect();
        const screenPos = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
        const worldPos = this.screenToWorldXY(screenPos);
        if (worldPos) {
          this.cameraAlignmentTool.updateEndPoint(worldPos);
        }
        return;
      }

      // Update area selection box
      if (this.isSelectingArea) {
        const rect = this.viewer.renderArea.getBoundingClientRect();
        this.selectionCurrentScreen = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
        this.updateSelectionBoxVisual();
        return;
      }
    }

    // Delegate to parent for floor selection hover
    if (this.stage === 'floor_selection') {
      super.onMouseMove(event);
    }
  }

  /**
   * Handle mouse up for selection stage - confirms the selection and starts detection
   */
  public onMouseUp(event: MouseEvent): void {
    if (this.stage !== 'selecting' || !this.isSelectingArea) return;
    if (event.button !== 0) return;

    this.isSelectingArea = false;

    // Check if selection is valid (minimum size)
    const areaM2 = this.calculateSelectionAreaM2();
    if (areaM2 < 1.0) {
      // Too small, cancel selection
      this.hideSelectionUI();
      this.selectionStartScreen = null;
      this.selectionStartWorld = null;
      this.selectionCurrentScreen = null;
      return;
    }

    // Create bounding box from selection
    // Project all 4 screen corners to world space to preserve rotation
    if (this.selectionStartScreen && this.selectionCurrentScreen) {
      const s = this.selectionStartScreen;
      const e = this.selectionCurrentScreen;

      // The 4 screen-space corners of the drawn rectangle
      const screenCorners = [
        new THREE.Vector2(s.x, s.y), // top-left of drawn rect
        new THREE.Vector2(e.x, s.y), // top-right
        new THREE.Vector2(e.x, e.y), // bottom-right
        new THREE.Vector2(s.x, e.y), // bottom-left
      ];

      const worldCorners = screenCorners.map((sc) => this.screenToWorldXY(sc));
      if (worldCorners.every((c): c is THREE.Vector3 => c !== null)) {
        const sceneBounds = this.viewer.getBoundingBox(this.viewer.scene.pointclouds);

        // Store the 4 actual world-space corners (preserves rotation)
        this.selectedAreaCorners = worldCorners;

        // Build an AABB from the corners for the detection grid sampling
        this.selectedBoundingBox = new THREE.Box3();
        for (const c of worldCorners) {
          this.selectedBoundingBox.expandByPoint(new THREE.Vector3(c.x, c.y, sceneBounds.min.z));
          this.selectedBoundingBox.expandByPoint(new THREE.Vector3(c.x, c.y, sceneBounds.max.z));
        }

        // Hide selection UI and start floor detection
        this.hideSelectionUI();
        this.stage = 'detecting';
        this.dispatchStateChanged({ phase: this.stage });
        this.detectFloors();
      }
    }
  }

  /**
   * Handle click events - camera alignment in selecting stage, floor selection in floor_selection stage
   */
  public onClick(event: MouseEvent): void {
    if (this.stage === 'selecting' && event.button === 0) {
      const rect = this.viewer.renderArea.getBoundingClientRect();
      const screenPos = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
      const worldPos = this.screenToWorldXY(screenPos);
      if (!worldPos) return;

      if (this.cameraAlignmentTool.isAlignmentActive()) {
        // Second click - complete alignment
        this.cameraAlignmentTool.complete(worldPos);
      } else {
        // First click - start alignment
        this.cameraAlignmentTool.start(worldPos);
      }
      return;
    }

    if (this.stage === 'floor_selection') {
      super.onClick(event);
    }
  }

  /**
   * Handle right-click to cancel alignment, selection, or preview
   */
  public onRightClick(event: MouseEvent): void {
    if (this.stage === 'selecting') {
      event.preventDefault();

      // Cancel camera alignment if active
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        this.cameraAlignmentTool.cancel();
        return;
      }

      // Cancel area selection if active
      if (this.isSelectingArea) {
        this.isSelectingArea = false;
        this.hideSelectionUI();
        this.selectionStartScreen = null;
        this.selectionStartWorld = null;
        this.selectionCurrentScreen = null;
        return;
      }
    }

    if (this.stage === 'floor_selection') {
      super.onRightClick(event);
    }
  }

  /**
   * Detect all floors in the selected area
   */
  private async detectFloors(): Promise<void> {
    if (!this.selectedBoundingBox) {
      this.stage = 'selecting';
      return;
    }

    // No caching when using area selection - always run fresh detection
    this.isDetecting = true;
    this.shouldCancelDetection = false;
    this.loadingWidget.show(this.viewer.translationService.t('Loading points in selected area...'));

    const session = ++this.detectionSession;
    setTimeout(async () => {
      if (!this.selectedBoundingBox) return;
      const boundingBox = this.selectedBoundingBox;

      // Phase 1: focus the whole point budget on the selection and wait for
      // the octree to finish refining there, so detection samples the area
      // at the highest density available. The clip stays on through the
      // detection itself — visibleNodes are rebuilt every frame and must
      // keep their focused density while the histogram accumulates.
      this.focusOnSelection(boundingBox);
      const loaded = await this.waitForFocusedLoad(boundingBox, session);
      // Superseded by a newer run (or disable): the successor owns cleanup.
      if (session !== this.detectionSession) return;

      let result: Awaited<ReturnType<typeof detectStoreys>> = null;
      if (loaded) {
        // Phase 2: detection over the densely loaded area, continuing the
        // same bar from the load share upward.
        this.loadingWidget.updateMessage(this.viewer.translationService.t('Detecting floors in selected area...'));
        this.loadingWidget.updateProgress(LOAD_PHASE_PROGRESS_PERCENT);
        try {
          result = await detectStoreys(this.viewer, {
            boundingBox,
            areaCorners: this.selectedAreaCorners?.map((c) => new THREE.Vector2(c.x, c.y)),
            onProgress: (progress) => {
              requestAnimationFrame(() => {
                this.loadingWidget.updateProgress(LOAD_PHASE_PROGRESS_PERCENT + (progress * (100 - LOAD_PHASE_PROGRESS_PERCENT)) / 100);
              });
            },
            shouldCancel: () => this.shouldCancelDetection,
          });
        } catch (error) {
          // A detection failure must never strand the loading overlay — surface
          // the error and fall back to the selection stage.
          console.error('[AutoFloorTool] Floor detection failed:', error);
          result = null;
        }
      }

      this.isDetecting = false;
      this.unfocusSelection();
      this.loadingWidget.hide();

      // If cancelled or tool was disabled while detecting, bail out
      if (result === null || this.shouldCancelDetection || !this.enabled) {
        if (this.enabled) {
          this.stage = 'selecting';
          this.dispatchStateChanged({ phase: this.stage });
          this.selectedBoundingBox = null;
          this.viewer.renderArea.style.cursor = 'crosshair';
        }
        return;
      }

      // Snapshot the run as a regression fixture NOW: the levels are edited
      // in place later (plane repicks, footprint drags) and those user
      // corrections must not read as pipeline output.
      this.lastDetectionFixture = result.debug.grid
        ? buildFloorDetectionFixture(
            result.debug.grid,
            result.levels,
            result.debug.tuning ?? FLOOR_DETECTION_TUNING,
            this.selectedAreaCorners?.map((c) => new THREE.Vector2(c.x, c.y)),
          )
        : null;

      // Storeys arrive with TRUE ceilings and per-level room patches;
      // sub-levels (mezzanines) are separate entries with their own footprint.
      this.availableFloors = result.levels.filter((level) => level.ceiling !== null);
      this.addedFloors = [];
      this.detectedFloors = this.availableFloors.map((level) => level.floor);

      visualizeDetectedLevels(this.viewer, this.availableFloors);

      // Save the detection area corners as a project property and set on the base class
      // Use the actual projected screen corners (preserves camera rotation)
      const corners = this.selectedAreaCorners || [
        new THREE.Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.min.z),
        new THREE.Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.min.z),
        new THREE.Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.max.z),
        new THREE.Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.max.z),
      ];
      this.detectionAreaCorners = corners;
      saveFloorDetectionArea(corners.map((c) => ({ x: c.x, y: c.y, z: c.z })) as [{ x: number; y: number; z: number }, { x: number; y: number; z: number }, { x: number; y: number; z: number }, { x: number; y: number; z: number }]);

      this.dispatchEvent({
        type: 'floors_detected',
        floors: this.detectedFloors,
      });

      // Transition to floor selection stage
      this.stage = 'floor_selection';
      this.dispatchStateChanged({ phase: this.stage });
      this.viewer.renderArea.style.cursor = 'default';

      // Refresh views to switch to 3D view for floor selection
      this.persona.refreshAvailableViews();

      this.createFloorVisualization();
    }, 100);
  }

  /**
   * Override: Create clipping plane, create floor group, and notify when a floor is confirmed
   */
  /** Proposals grey, added levels green on the elevation map. */
  protected mutesProposalBars(): boolean {
    return true;
  }

  /** Double clicking a proposed level's bar adds it (a single click only previews it). */
  protected onFloorBarDoubleClicked(floorIndex: number): void {
    this.previewEditRig?.cancelRepick();
    this.confirmFloorSelection(floorIndex);
  }

  protected onFloorConfirmed(floor: DetectedFloorPlane, ceiling: DetectedFloorPlane | undefined, floorsRemaining: number, footprintCorners: THREE.Vector2[]): void {
    // Hide the confirmed floor with a SHOW_OUTSIDE clip BOX scoped to the
    // level's own footprint — a plane clip would blank the whole z-band
    // across the scene. The hidden band runs up to the next PROPOSAL's floor
    // (fallback: true ceiling + slab allowance) so no un-hidden slab stripe
    // remains between stacked confirmed levels.
    if (ceiling && footprintCorners.length >= 4) {
      const nextProposalFloorZ = this.availableFloors.reduce<number | null>((next, level) => {
        const h = level.floor.height;
        if (h > floor.height + 1e-4 && (next === null || h < next)) return h;
        return next;
      }, null);
      const hideTop = nextProposalFloorZ ?? ceiling.height + DEFAULT_SLAB_THICKNESS_M;

      // Box from the (possibly rotated) footprint corners × the hidden band
      const [c0, c1, , c3] = footprintCorners;
      const edgeX = new THREE.Vector2().subVectors(c1, c0);
      const edgeY = new THREE.Vector2().subVectors(c3, c0);
      const center = new THREE.Vector2().addVectors(c0, footprintCorners[2]).multiplyScalar(0.5);

      const hideBox = new BoxVolume({
        position: new THREE.Vector3(center.x, center.y, (floor.height + hideTop) / 2),
        scale: new THREE.Vector3(edgeX.length(), edgeY.length(), hideTop - floor.height),
        rotation: new THREE.Vector3(0, 0, Math.atan2(edgeX.y, edgeX.x)),
        clip: true,
      });
      hideBox.assignedClipTask = ClipTask.SHOW_OUTSIDE;
      hideBox.actualClipTask = ClipTask.SHOW_OUTSIDE;
      hideBox.visible = false; // clip only — no box visuals
      hideBox.updateMatrixWorld(true);
      this.viewer.scene.addVolume(hideBox);
      this.selectedFloorClipBoxes.push(hideBox);
    }

    // Dispatch create_floor event to create the floor group. The footprint
    // corners persist as the floor's OWN bounding area (floor_footprint_corner
    // properties), replacing reliance on the global detection rect.
    this.viewer.scene.dispatchEvent({
      type: 'create_floor',
      value: {
        floor: floor,
        ceiling: ceiling,
        footprint: footprintCorners.map((c) => ({ x: c.x, y: c.y, z: floor.height })),
      },
    });

    // Dispatch notification
    this.viewer.scene.dispatchEvent({
      type: 'floor_added_notification',
      value: {
        floorHeight: floor.height.toFixed(2),
        floorsRemaining: floorsRemaining,
      },
    });

    // Update quest hint: all proposals added → guide user to room creation
    if (floorsRemaining === 0) {
      this.stage = 'all_added';
      this.dispatchStateChanged({ phase: this.stage });
    }
  }

  /**
   * Override: Clean up clipping planes when tool is disabled
   */
  protected onCleanup(): void {
    // Remove all confirmed-floor clip boxes
    this.selectedFloorClipBoxes.forEach((box) => {
      this.viewer.scene.removeVolume(box);
    });
    this.selectedFloorClipBoxes = [];
  }

  /**
   * Override: Handle keyboard events
   */
  public onKeyDown(event: KeyboardEvent): void {
    // Handle escape key based on current stage
    if (event.key === 'Escape') {
      event.preventDefault();

      if (this.isDetecting || this.stage === 'detecting') {
        // Cancel ongoing detection
        this.shouldCancelDetection = true;
        return;
      }

      if (this.stage === 'selecting' && this.cameraAlignmentTool.isAlignmentActive()) {
        // Cancel camera alignment
        this.cameraAlignmentTool.cancel();
        return;
      }

      if (this.stage === 'selecting' && this.isSelectingArea) {
        // Cancel current selection drag
        this.isSelectingArea = false;
        this.hideSelectionUI();
        this.selectionStartScreen = null;
        this.selectionStartWorld = null;
        this.selectionCurrentScreen = null;
        return;
      }

      if (this.stage === 'selecting' || this.stage === 'all_added') {
        // Exit tool entirely
        this.persona.enableTool('floor_selector');
        return;
      }

      if (this.stage === 'floor_selection') {
        // Go back to selection stage
        this.stage = 'selecting';
        this.dispatchStateChanged({ phase: this.stage });
        this.selectedBoundingBox = null;
        this.selectedAreaCorners = null;
        this.detectedFloors = [];
        this.availableFloors = [];
        this.addedFloors = [];
        this.clearFloorVisualization();
        this.clearPreviewOutline();
        this.viewer.renderArea.style.cursor = 'crosshair';
        this.persona.refreshAvailableViews();
        return;
      }
    }

    // Let parent class handle other keys for floor selection stage
    if (this.stage === 'floor_selection') {
      super.onKeyDown(event);
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.cameraAlignmentTool.dispose();
    super.dispose();
  }

  /**
   * Provide custom view configuration based on current stage
   */
  getViewConfig(): IViewConfig {
    // During selection stage, force 2D top-down view
    if (this.stage === 'selecting' || this.stage === 'detecting') {
      return createMultiViewConfig([ViewModes.XRAY_2D], ViewModes.XRAY_2D.id);
    }

    // During floor selection, use 3D view
    return createMultiViewConfig([ViewModes.PERSPECTIVE_3D_RGB], ViewModes.PERSPECTIVE_3D_RGB.id);
  }
}
